import { useCallback, useMemo, useState } from 'react';

import { Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FilterChips } from '@/components/data-table/filters-v2/filter-chips';
import { FilterDropdown } from '@/components/data-table/filters-v2/filter-dropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger/logger';

import { useDebouncedSearchFilter } from './hooks/use-debounced-search-filter';
import { useTableFilters } from './hooks/use-table-filters';
import { SaveFiltersModal } from './modal/save-filters-modal';
import { SavedFilter, SearchFilterConfig, TableFiltersProps } from './types/types';

const TAG = 'DynamicAutocompleteFilterSubmenu:';
/**
 * Generic table filters component with dropdown, chips, and save functionality
 *
 * Can be used in two ways:
 * 1. Standalone (manages its own state via URL):
 *    <TableFilters filtersConfig={config} tableId="my-table" />
 *
 * 2. Controlled (parent provides filter state from useTableFilters):
 *    const filters = useTableFilters(config);
 *    <TableFilters filtersConfig={config} tableId="my-table" filterState={filters} />
 *    // Access filters.activeFilters for API calls
 */
export function DataTableFiltersV2({
  filtersConfig,
  tableId,
  showSaveButton = false, //TODO need to revert it to true once save functionality will be implemented
  showClearButton = true,
  className,
  filterState,
  onFilterValueChange,
  onSearchFocus,
  dropdownOpen: controlledDropdownOpen,
  onDropdownOpenChange,
  leadingControls,
}: TableFiltersProps) {
  const { t } = useTranslation('common');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [savedFilters] = useState<SavedFilter[]>([]); // TODO: Load from API
  // Controlled dropdown open + focused filter so clicking a chip opens its submenu
  const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);
  const dropdownOpen = controlledDropdownOpen ?? internalDropdownOpen;
  const [focusedFilterName, setFocusedFilterName] = useState<string | null>(null);

  // Use provided filter state or create internal one
  const internalFilterState = useTableFilters(filtersConfig);
  const {
    activeFilters,
    hasActiveFilters,
    setFilter,
    clearFilter,
    clearAllFilters,
    applyFilters,
    removeFilterValue,
  } = filterState || internalFilterState;

  // Get search filter config if exists
  const searchConfig = useMemo(
    () => filtersConfig.find(c => c.type === 'search') as SearchFilterConfig | undefined,
    [filtersConfig]
  );

  // Handle filter change from dropdown. An optional veto callback can block the
  // change (e.g. to prompt an unsaved-changes guard before applying).
  const handleFilterChange = useCallback(
    (name: string, value: string | string[]) => {
      if (onFilterValueChange?.(name, value) === false) {
        return;
      }
      setFilter(name, value);
    },
    [setFilter, onFilterValueChange]
  );

  // Debounce search keystrokes + enforce minCharacters so each character doesn't
  // trip the URL filter → query key → a backend call. See the hook for details.
  const committedSearchValue = searchConfig
    ? ((activeFilters[searchConfig.name] as string) ?? '')
    : '';
  const { inputValue: searchInputValue, onInputChange: handleSearchInputChange } =
    useDebouncedSearchFilter({
      committedValue: committedSearchValue,
      minCharacters: searchConfig?.minCharacters,
      onCommit: value => searchConfig && handleFilterChange(searchConfig.name, value),
    });

  // Clicking a filter chip opens the dropdown and auto-expands that filter's submenu
  const handleChipClick = useCallback(
    (name: string) => {
      setFocusedFilterName(name);
      setInternalDropdownOpen(true);
      onDropdownOpenChange?.(true);
    },
    [onDropdownOpenChange]
  );

  const handleDropdownOpenChange = useCallback(
    (next: boolean) => {
      setInternalDropdownOpen(next);
      onDropdownOpenChange?.(next);
      // Reset the focused filter when the dropdown closes
      if (!next) {
        setFocusedFilterName(null);
      }
    },
    [onDropdownOpenChange]
  );

  // Handle applying saved filter
  const handleApplySavedFilter = useCallback(
    (filter: SavedFilter) => {
      applyFilters(filter.filters);
    },
    [applyFilters]
  );

  // Handle save filter
  const handleSaveFilter = useCallback(
    async (filter: Omit<SavedFilter, 'id' | 'createdAt' | 'createdBy'>) => {
      // TODO: Implement API call to save filter
      logger.info(TAG, 'Save filter:', { ...filter, tableId });
    },
    [tableId]
  );

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Filter controls row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* External controls (e.g. a search input) share the wrap row so chips flow under them */}
        {leadingControls}

        {/* Search input (if configured) */}
        {searchConfig && (
          <Input
            placeholder={searchConfig.placeholder || t('search')}
            value={searchInputValue}
            onChange={e => handleSearchInputChange(e.target.value)}
            onFocus={e => {
              // Allow the parent to veto focus (e.g. unsaved-changes guard) by blurring
              if (onSearchFocus?.(e) === false) {
                e.target.blur();
              }
            }}
            className="h-9 w-[220px]"
          />
        )}

        {/* Filter dropdown button */}
        <FilterDropdown
          filtersConfig={filtersConfig}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onClearFilter={clearFilter}
          savedFilters={savedFilters}
          onApplySavedFilter={handleApplySavedFilter}
          open={dropdownOpen}
          onOpenChange={handleDropdownOpenChange}
          focusedFilterName={focusedFilterName}
          onFocusedFilterHandled={() => setFocusedFilterName(null)}
        />

        {/* Save button */}
        {showSaveButton && hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={() => setSaveModalOpen(true)}
            title={t('tableFilters.saveFilters')}
          >
            <Save className="h-4 w-4" />
          </Button>
        )}

        {/* Active filter chips */}
        <FilterChips
          filtersConfig={filtersConfig}
          activeFilters={activeFilters}
          onRemoveFilter={removeFilterValue}
          onClearAll={clearAllFilters}
          onChipClick={handleChipClick}
          showClearButton={showClearButton}
        />
      </div>

      {/* Save filters modal */}
      <SaveFiltersModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSave={handleSaveFilter}
        filtersConfig={filtersConfig}
        activeFilters={activeFilters}
      />
    </div>
  );
}
